import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type React from 'react';
import { DayPicker } from 'react-day-picker';
import { Button, ButtonGroup, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';
import { buttonVariants } from './Button';

import { type CaptionProps, useNavigation } from 'react-day-picker';

function CalendarCaption({ displayMonth, fromYear, toYear }: CaptionProps & { fromYear: number; toYear: number }) {
  const { goToMonth } = useNavigation();

  const month = displayMonth.getMonth();
  const year = displayMonth.getFullYear();

  const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i);

  const handlePrevMonth = () => {
    let newMonth = month - 1;
    let newYear = year;

    if (newMonth < 0) {
      newMonth = 11;
      newYear = year - 1;
    }

    if (newYear >= fromYear) {
      goToMonth(new Date(newYear, newMonth));
    }
  };

  const handleNextMonth = () => {
    let newMonth = month + 1;
    let newYear = year;

    if (newMonth > 11) {
      newMonth = 0;
      newYear = year + 1;
    }

    if (newYear <= toYear) {
      goToMonth(new Date(newYear, newMonth));
    }
  };

  return (
    <div className="flex items-center justify-center w-full gap-1">
      <Button onClick={handlePrevMonth} variant="ghost" size="sm" aria-label="Previous month">
        <ChevronLeftIcon />
      </Button>

      <ButtonGroup>
        <Select value={String(month)} onValueChange={(value) => goToMonth(new Date(year, Number(value)))} modal={false}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 12 }).map((_, i) => (
              <SelectItem key={i} value={String(i)}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={String(year)} onValueChange={(value) => goToMonth(new Date(Number(value), month))} modal={false}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ButtonGroup>

      <Button onClick={handleNextMonth} variant="ghost" size="sm" aria-label="Next month">
        <ChevronRightIcon />
      </Button>
    </div>
  );
}

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  fromYear?: number;
  toYear?: number;
};

function Calendar({ className, classNames, showOutsideDays = true, fromYear = 1900, toYear = new Date().getFullYear() + 10, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3 !m-0 bg-background rounded border border-border', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'hidden',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(buttonVariants({ variant: 'outline' }), 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 shadow-none hover:!bg-primary hover:text-background'),
        nav_button_previous: '!absolute left-1',
        nav_button_next: '!absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex justify-center',
        head_cell: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        row: 'flex w-full mt-1 justify-center',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-primary [&:has([aria-selected].day-outside)]:bg-primary/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md',
        ),
        day: cn(buttonVariants({ variant: 'ghost' }), 'h-8 w-8 p-0 font-normal aria-selected:opacity-100'),
        day_range_start: 'day-range-start rounded-r-none',
        day_range_end: 'day-range-end rounded-l-none',
        day_selected: 'bg-primary !text-primary-foreground hover:!bg-primary hover:!text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today:
          'bg-accent text-accent-foreground ring-2 ring-inset ring-blue-500 rounded-lg aria-selected:bg-blue-500 aria-selected:hover:!bg-blue-600 aria-selected:hover:!ring-blue-600 aria-selected:hover:!bg-blue-500 hover:!bg-blue-500/50',
        day_outside: 'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle: 'aria-selected:bg-primary/20 aria-selected:text-accent-foreground aria-selected:rounded-none',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Caption: (captionProps) => <CalendarCaption {...captionProps} fromYear={fromYear} toYear={toYear} />,
        IconLeft: ({ className, ...props }) => <ChevronLeftIcon className={cn('h-4 w-4', className)} {...props} />,
        IconRight: ({ className, ...props }) => <ChevronRightIcon className={cn('h-4 w-4', className)} {...props} />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
